import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { Button, Fade, Icon, Input, Modal, ModalSize } from '@column/column-ui-kit';

import { TransferPageHeader } from '../../_components/Layout';
import { CopyRow } from '~/app/pages/Transfers/_components/CopyRow';
import { RenderFields } from '~/components';
import { LogoLoading } from '~/elements';
import { useBankAccount } from '~/hooks/useBankAccounts';
import { useCounterparty } from '~/hooks/useCounterparties';
import { useTransfer } from '~/hooks/useTransfers';
import { useNavigate } from '~/lib/navigation';
import { getTransferURL } from '~/lib/transfers/utils';
import { AchTransfer, Counterparty, TransferEntryType, convertTransferAmount } from '~/repositories';
import { BankAccount } from '~/repositories/BankAccountRepository';
import { Card, Divider, Flex, Inner, Text } from '~/styles';
import { UnifiedTransfer, WireTransfer } from '~/typings/API';
import { ReviewTransferRequest, TransferReviewDecision } from '~/typings/enum';

import { PageCounter } from './PageCounter';

interface TransferReviewModalProps {
  currentIndex: number;
  transfers: UnifiedTransfer[];
  totalTransfers: number;
  isOpen: boolean;
  onClose: () => void;
  onNextTransfer: () => void;
  onPreviousTransfer: () => void;
  onSubmit: (transferId: string, transferType: TransferEntryType, request: ReviewTransferRequest) => Promise<void>;
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: auto;
  gap: 4px;
  padding: 12px 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 16px 0;
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: auto min-content;
    align-items: center;
    gap: 0;
  }
`;

const ViewTransferButton = styled(Button)`
  padding: 0;
  margin-bottom: 3px;
`;

const EmptyStateWrapper = styled(Fade)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: calc(100% - 24px);

  div {
    height: 100%;
  }
`;

const KeyboardArrow = styled.div`
  border: 1px solid ${({ theme }) => theme.secondary.blendToBackground(300)};
  padding: 3px;
  border-radius: 4px;

  svg {
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(500)};
    width: 12px;
    height: 12px;
  }
`;

const LoadingContainer = styled.div<{ show: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 32px;
  opacity: ${({ show }) => (show ? 1 : 0)};
  transition: opacity 0.2s ease;
  pointer-events: none;
`;

const AnimatedButtonContainer = styled.div<{ animationState: 'in' | 'out' | undefined }>`
  position: relative;
  display: inline-block;

  transition: all 0.3s ease;
  transform: translate(${({ animationState }) => (!!animationState ? '0, -4px' : '0, 0')});

  svg {
    --icon-color: ${({ theme }) => theme.white.background};
  }

  .button-content {
    opacity: ${({ animationState }) => (animationState === 'in' ? 0 : 1)};
    transition: opacity 0.3s ease;
    transition-delay: ${({ animationState }) => (!!animationState ? '0.3s' : '0')};
    visibility: ${({ animationState }) => (animationState === 'in' ? 'hidden' : 'visible')};
  }

  .icon {
    position: absolute;
    pointer-events: none;
    top: 50%;
    left: 50%;
    transform: translate(${({ animationState }) => (!!animationState ? '-50%, -50% ' : '-50%, -25%')})
      scale(${({ animationState }) => (animationState === 'in' ? 1.2 : animationState === 'out' ? 1.8 : 0.6)});
    opacity: ${({ animationState }) => (animationState === 'in' ? 1 : 0)};
    transition: all 0.3s ease;
  }
`;

const StyledCard = styled(Card)`
  .transfer-page-header {
    padding: 16px;
  }
`;

interface AnimatedButtonProps {
  onClick: () => void;
  isLoading: boolean;
  isDisabled: boolean;
  animationState: 'in' | 'out' | undefined;
  children: React.ReactNode;
  icon: React.ReactNode;
  variant?: 'primary' | 'danger';
}

const AnimatedButton: React.FC<AnimatedButtonProps> = ({
  onClick,
  isLoading,
  isDisabled,
  animationState,
  children,
  icon,
  variant,
}) => (
  <AnimatedButtonContainer animationState={animationState}>
    <Button variant={variant} size="small" isLoading={isLoading} isDisabled={isDisabled} onClick={onClick}>
      <span className="button-content">{children}</span>
    </Button>
    <div className="icon">{icon}</div>
  </AnimatedButtonContainer>
);

const getOptionKey = () => {
  const isMac = navigator.userAgent.includes('Mac');
  return isMac ? '⌥' : 'Alt+';
};

export const TransferReviewModal: React.FC<TransferReviewModalProps> = ({
  currentIndex: currentIndexProp,
  transfers: transfersProp,
  totalTransfers: totalTransfersProp,
  isOpen,
  onClose,
  onSubmit,
  onNextTransfer,
  onPreviousTransfer,
}) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState<TransferReviewDecision>();
  const [showContent, setShowContent] = useState(true);
  const [slideDirection, setSlideDirection] = useState<'up' | 'down'>('up');
  const formMethods = useForm<ReviewTransferRequest>();
  const comment = formMethods.watch('comment');
  const commentFocused = useRef(false);

  const [currentIndex, setCurrentIndex] = useState(currentIndexProp);
  const [transfers, setTransfers] = useState<UnifiedTransfer[]>(transfersProp);
  const [totalTransfers, setTotalTransfers] = useState(totalTransfersProp);
  const [bankAccounts, setBankAccounts] = useState<{ [bankAccountId: string]: BankAccount }>({});
  const [counterparties, setCounterparties] = useState<{ [counterpartyId: string]: Counterparty }>({});
  const [creators, setCreators] = useState<{ [transferId: string]: string | undefined }>({});
  const currentData = useMemo(() => {
    const transfer = transfers[currentIndex];
    return {
      bankAccount: bankAccounts[transfer?.senderInternalAccount.bankAccountId],
      counterparty: counterparties[transfer?.externalDestination.counterpartyId],
      creator: creators[transfer?.id],
      transfer,
    };
  }, [bankAccounts, counterparties, currentIndex, transfers]);
  const emptyState = transfers.length === 0;
  const isLoadingAdditionalData = !emptyState && (!currentData.bankAccount || !currentData.counterparty);
  useEffect(() => {
    if (currentData.bankAccount && currentData.counterparty) {
      setShowContent(true);
    }
  }, [currentData]);
  useEffect(() => {
    if (isSubmitting) return;
    commentFocused.current = false;
    setCurrentIndex(currentIndexProp);
    setTotalTransfers(totalTransfersProp);

    if (transfers[currentIndex]?.id === transfersProp[currentIndex]?.id) {
      setTransfers(transfersProp);
    } else {
      setShowContent(false);
      setTimeout(() => {
        setTransfers(transfersProp);
        setShowContent(true);
      }, 200);
    }
  }, [currentIndex, currentIndexProp, isSubmitting, transfers, transfersProp]);

  const { createRequest: fetchAchTransfer } = useTransfer<AchTransfer>(
    'ach',
    false
  )({
    onSuccess: (response) => {
      setCreators((prev) => ({
        ...prev,
        [response.id]: response.createdBy?.name,
      }));
    },
  });

  const { createRequest: fetchWireTransfer } = useTransfer<WireTransfer>(
    'wire',
    false
  )({
    onSuccess: (response) => {
      setCreators((prev) => ({
        ...prev,
        [response.id]: response.createdBy?.name,
      }));
    },
  });

  const { createRequest: fetchBankAccount } = useBankAccount({
    onSuccess: (response) => {
      setBankAccounts((prev) => ({
        ...prev,
        [response.id]: response,
      }));
    },
  });

  const { createRequest: fetchCounterparty } = useCounterparty({
    onSuccess: (response) => {
      if (!response.id) return;
      setCounterparties((prev) => ({
        ...prev,
        [response.id as string]: response,
      }));
    },
  });

  const loadTransferData = useCallback(
    async (transfer: UnifiedTransfer) => {
      if (!transfer) return;

      if (!creators[transfer.id]) {
        if (transfer.type === 'ach_credit' || transfer.type === 'ach_debit') {
          fetchAchTransfer({ id: transfer.id });
        } else if (transfer.type === 'wire') {
          fetchWireTransfer({ id: transfer.id });
        }
      }

      if (transfer.senderInternalAccount.bankAccountId && !bankAccounts[transfer.senderInternalAccount.bankAccountId]) {
        fetchBankAccount({ id: transfer.senderInternalAccount.bankAccountId });
      }
      if (transfer.externalDestination.counterpartyId && !counterparties[transfer.externalDestination.counterpartyId]) {
        fetchCounterparty({ id: transfer.externalDestination.counterpartyId });
      }
    },
    [bankAccounts, counterparties, fetchBankAccount, fetchCounterparty]
  );

  useEffect(() => {
    if (transfers[currentIndex]) loadTransferData(transfers[currentIndex]);
    if (currentIndex > 0 && transfers[currentIndex - 1]) {
      loadTransferData(transfers[currentIndex - 1]);
    }
    if (currentIndex < totalTransfers - 1 && transfers[currentIndex + 1]) {
      loadTransferData(transfers[currentIndex + 1]);
    }
  }, [currentIndex, isOpen, transfers, totalTransfers]);

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      commentFocused.current = false;
      setSlideDirection('down');
      setShowContent(false);

      setTimeout(() => {
        formMethods.reset();
        onPreviousTransfer();
        setTimeout(() => {
          setShowContent(true);
        }, 100);
      }, 100);
    }
  }, [currentIndex]);

  const handleNext = useCallback(() => {
    if (currentIndex < totalTransfers - 1) {
      commentFocused.current = false;
      setSlideDirection('up');
      setShowContent(false);

      setTimeout(() => {
        onNextTransfer();
        formMethods.reset();
        setTimeout(() => {
          setShowContent(true);
        }, 100);
      }, 100);
    }
  }, [currentIndex, totalTransfers]);

  const [successAnimation, setSuccessAnimation] = useState<'in' | 'out' | undefined>(undefined);
  const [rejectAnimation, setRejectAnimation] = useState<'in' | 'out' | undefined>(undefined);

  const handleSubmit = useCallback(
    async (decision: TransferReviewDecision) => {
      setIsSubmitting(decision);

      try {
        await onSubmit(currentData.transfer?.id, currentData.transfer?.type, {
          comment,
          decision,
        });

        if (decision === TransferReviewDecision.Approved) {
          setSuccessAnimation('in');
          await new Promise((resolve) => setTimeout(resolve, 600));
          setSuccessAnimation('out');
          await new Promise((resolve) => setTimeout(resolve, 300));
        } else if (decision === TransferReviewDecision.Rejected) {
          setRejectAnimation('in');
          await new Promise((resolve) => setTimeout(resolve, 600));
          setRejectAnimation('out');
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      } finally {
        setIsSubmitting(undefined);
        setSuccessAnimation(undefined);
        setRejectAnimation(undefined);
      }

      formMethods.reset();
    },
    [currentData, currentIndex, totalTransfers, comment, onSubmit, handleNext, handlePrevious, onClose]
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const isAltOption = e.altKey;

      if (isLoadingAdditionalData || isSubmitting) return;

      if (isAltOption) {
        if (e.code === 'KeyA') {
          e.preventDefault();
          handleSubmit(TransferReviewDecision.Approved);
        }
        if (e.code === 'KeyR') {
          e.preventDefault();
          handleSubmit(TransferReviewDecision.Rejected);
        }
      } else if (!commentFocused.current) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          handleNext();
        }
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          handlePrevious();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isLoadingAdditionalData, isSubmitting, handleSubmit, handleNext, handlePrevious]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={ModalSize.Large} withClose={false}>
      <Modal.Content align="left">
        <Grid>
          <Modal.Headline>Review Transfer</Modal.Headline>
          <Fade show={!emptyState} direction={slideDirection === 'up' ? 'right' : 'left'}>
            <Flex alignItems="center" flexDirection="row" gap={6} justifyContent="space-between">
              <Flex alignItems="center" flexDirection="row" gap={12}>
                <Flex alignItems="center" flexDirection="row" gap={6}>
                  <Text size={12} color="lighter">
                    Previous
                  </Text>
                  <KeyboardArrow>
                    <Icon.ArrowLeft />
                  </KeyboardArrow>
                </Flex>
                <Flex alignItems="center" flexDirection="row" gap={6}>
                  <KeyboardArrow>
                    <Icon.ArrowRight />
                  </KeyboardArrow>
                  <Text size={12} color="lighter">
                    Next
                  </Text>
                </Flex>
              </Flex>
              <PageCounter
                current={currentIndex + 1}
                total={totalTransfers}
                direction={slideDirection}
                onPrevious={handlePrevious}
                onNext={handleNext}
              />
            </Flex>
          </Fade>
        </Grid>
      </Modal.Content>

      <Divider />

      <Modal.Content align="left">
        <LoadingContainer show={(transfers.length > 0 && !currentData.transfer) || isLoadingAdditionalData}>
          <LogoLoading />
        </LoadingContainer>

        <EmptyStateWrapper show={emptyState} distance={24} direction="right">
          <Flex alignItems="center" flexDirection="column" gap={8} justifyContent="center">
            <Text size={18} weight="medium" color="foreground">
              All caught up
            </Text>
            <Text size={14} color="light">
              There are no more transfers to review
            </Text>
          </Flex>
        </EmptyStateWrapper>

        <Fade
          key={currentIndex}
          show={!emptyState && !isLoadingAdditionalData && showContent}
          distance={16}
          direction={slideDirection === 'up' ? 'right' : 'left'}
        >
          <Inner px={0} pt="var(--page-margin)" pb={0}>
            <Flex flexDirection="column" gap={24}>
              <StyledCard>
                <TransferPageHeader
                  className="transfer-page-header"
                  action={
                    <ViewTransferButton
                      icon={<Icon.ChevronRightLarge />}
                      iconRight
                      variant="subtle"
                      onClick={() => navigate(getTransferURL(currentData.transfer?.id, currentData.transfer?.type))}
                    >
                      View Transfer
                    </ViewTransferButton>
                  }
                  amount={currentData.transfer?.amount}
                  createdAt={currentData.transfer?.createdAt}
                  currencyCode={currentData.transfer?.currencyCode}
                  direction={{ type: 'outgoing' }}
                  isAdding={
                    currentData.transfer?.type
                      ? convertTransferAmount(currentData.transfer.type, currentData.transfer.isIncoming) > 0
                      : undefined
                  }
                  transferType={currentData.transfer?.type}
                />
                <CopyRow label="Account" value={currentData.bankAccount?.description || '-'} />
                <CopyRow label="Counterparty" value={currentData.counterparty?.name || '-'} />
                <CopyRow label="Created By" value={currentData.creator || '-'} />
                <CopyRow label="Description" value={currentData.transfer?.description || '-'} />
              </StyledCard>

              <FormProvider {...formMethods}>
                {RenderFields<ReviewTransferRequest>({
                  sections: [
                    {
                      variant: 'isCompact',
                      fields: [
                        {
                          id: 'comment',
                          label: 'Comment',
                          oneColumn: true,
                          children: ({ onChange, value }, { error }) => (
                            <Input
                              isDisabled={!!isSubmitting}
                              onChange={onChange}
                              onFocus={() => (commentFocused.current = true)}
                              onBlur={() => (commentFocused.current = false)}
                              placeholder="Add a comment..."
                              value={value as string}
                              hasError={!!error}
                            />
                          ),
                        },
                      ],
                    },
                  ],
                })}
              </FormProvider>
            </Flex>
          </Inner>
        </Fade>
      </Modal.Content>

      <Modal.Footer>
        <Button variant="secondary" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Flex alignItems="center" flexDirection="row" gap={12} justifyContent="flex-end" auto>
          <AnimatedButton
            onClick={() => handleSubmit(TransferReviewDecision.Rejected)}
            isLoading={isSubmitting === TransferReviewDecision.Rejected && !rejectAnimation}
            isDisabled={isSubmitting === TransferReviewDecision.Approved || emptyState}
            animationState={rejectAnimation}
            icon={<Icon.Cross />}
            variant="danger"
          >
            Reject <span style={{ marginLeft: 8, opacity: 0.6, fontSize: 12 }}>{getOptionKey()}R</span>
          </AnimatedButton>
          <AnimatedButton
            onClick={() => handleSubmit(TransferReviewDecision.Approved)}
            isLoading={isSubmitting === TransferReviewDecision.Approved && !successAnimation}
            isDisabled={isSubmitting === TransferReviewDecision.Rejected || emptyState}
            animationState={successAnimation}
            icon={<Icon.Checkmark />}
          >
            Approve <span style={{ marginLeft: 8, opacity: 0.6, fontSize: 12 }}>{getOptionKey()}A</span>
          </AnimatedButton>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
};
